<template>
  <div class="px-4 pt-6">
    <h1 class="text-2xl font-bold text-gray-900 dark:text-white mb-4">{{ docHeading }}</h1>

    <div class="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl shadow-sm dark:from-gray-800 dark:to-gray-700 dark:border-gray-600 mb-6 overflow-hidden">
      <div class="px-6 py-4 bg-blue-600 dark:bg-gray-700">
        <h2 class="text-lg font-semibold text-white flex items-center">
          <svg class="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M4 4a2 2 0 00-2 2v4a2 2 0 002 2V6h10a2 2 0 00-2-2H4zm2 6a2 2 0 012-2h8a2 2 0 012 2v4a2 2 0 01-2 2H8a2 2 0 01-2-2v-4zm6 4a2 2 0 100-4 2 2 0 000 4z" clip-rule="evenodd"/>
          </svg>
          {{ docDetailsTitle }}
        </h2>
      </div>
      <div class="p-6 space-y-4">
        <div :class="metaGridClass">
          <div class="flex items-center space-x-3 p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-100 dark:border-gray-600">
            <div class="flex-shrink-0">
              <div class="w-8 h-8 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center">
                <svg class="w-4 h-4 text-blue-600 dark:text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M4 4a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2H4zm0 2h12v8H4V6z" clip-rule="evenodd"/>
                </svg>
              </div>
            </div>
            <div class="flex-1 min-w-0">
              <p class="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">{{ docIdLabel }}</p>
              <p class="text-sm font-semibold truncate">
                <a
                  v-if="isPurchaseOrder && erpBaseUrl"
                  :href="erpBaseUrl + '/app/purchase-order/' + receiptName"
                  target="_blank"
                  rel="noopener"
                  class="text-blue-600 hover:underline dark:text-blue-400"
                >{{ receiptName }}</a>
                <span v-else class="text-gray-900 dark:text-white">{{ receiptName }}</span>
              </p>
            </div>
          </div>
          
          <div v-if="meta" class="flex items-center space-x-3 p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-100 dark:border-gray-600">
            <div class="flex-shrink-0">
              <div class="w-8 h-8 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center">
                <svg class="w-4 h-4 text-green-600 dark:text-green-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a1 1 0 110 2h-3a1 1 0 01-1-1v-6a1 1 0 00-1-1H9a1 1 0 00-1 1v6a1 1 0 01-1 1H4a1 1 0 110-2V4z" clip-rule="evenodd"/>
                </svg>
              </div>
            </div>
            <div class="flex-1 min-w-0">
              <p class="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Supplier</p>
              <p class="text-sm font-semibold text-gray-900 dark:text-white truncate">{{ supplierDisplay(meta) }}</p>
            </div>
          </div>

          <div
            v-if="meta && isPurchaseOrder && (meta.customer_name || '').trim()"
            class="flex items-center space-x-3 p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-100 dark:border-gray-600"
          >
            <div class="flex-shrink-0">
              <div class="w-8 h-8 bg-amber-100 dark:bg-amber-900/40 rounded-full flex items-center justify-center">
                <svg class="w-4 h-4 text-amber-700 dark:text-amber-400" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z"/>
                </svg>
              </div>
            </div>
            <div class="flex-1 min-w-0">
              <p class="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Customer</p>
              <p class="text-sm font-semibold text-gray-900 dark:text-white truncate">{{ (meta.customer_name || '').trim() }}</p>
            </div>
          </div>
          
          <div v-if="meta" class="flex items-center space-x-3 p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-100 dark:border-gray-600">
            <div class="flex-shrink-0">
              <div class="w-8 h-8 bg-purple-100 dark:bg-purple-900 rounded-full flex items-center justify-center">
                <svg class="w-4 h-4 text-purple-600 dark:text-purple-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clip-rule="evenodd"/>
                </svg>
              </div>
            </div>
            <div class="flex-1 min-w-0">
              <p class="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Created</p>
              <p class="text-sm font-semibold text-gray-900 dark:text-white truncate">{{ formatDate(meta.creation) }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Purchase Order: ordered line items -->
    <div
      v-if="isPurchaseOrder"
      class="p-4 bg-white border mt-4 border-gray-200 rounded-lg shadow-sm dark:border-gray-700 sm:p-6 dark:bg-gray-800"
    >
      <h3 class="text-xl font-bold text-gray-900 dark:text-white mb-2">
        Ordered on this PO
        <span v-if="isSubcontracted" class="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-300">Subcontracted</span>
      </h3>
      <p v-if="poDetailLoading" class="text-sm text-gray-500 dark:text-gray-400">Loading order lines…</p>
      <p v-else-if="poDetailError" class="text-sm text-red-600 dark:text-red-400">{{ poDetailError }}</p>
      <div v-else-if="poItems.length">
        <p
          v-if="poDetailMeta"
          class="text-sm text-gray-600 dark:text-gray-400 mb-3"
        >
          <span v-if="poDetailMeta.status">Status: <strong>{{ poDetailMeta.status }}</strong></span>
          <span v-if="poDetailMeta.transaction_date" class="ml-3">Date: {{ formatDate(poDetailMeta.transaction_date) }}</span>
        </p>
        <div class="overflow-x-auto">
        <table class="w-full divide-y divide-gray-200 dark:divide-gray-600">
          <thead class="bg-gray-100 dark:bg-gray-700">
            <tr>
              <th class="p-3 text-left text-xs font-medium text-gray-500 uppercase dark:text-gray-400">Item code</th>
              <th v-if="isSubcontracted" class="p-3 text-left text-xs font-medium text-gray-500 uppercase dark:text-gray-400">FG item</th>
              <th class="p-3 text-left text-xs font-medium text-gray-500 uppercase dark:text-gray-400">Item name</th>
              <th class="p-3 text-right text-xs font-medium text-gray-500 uppercase dark:text-gray-400">Qty</th>
              <th v-if="isSubcontracted" class="p-3 text-right text-xs font-medium text-gray-500 uppercase dark:text-gray-400">FG qty</th>
              <th class="p-3 text-right text-xs font-medium text-gray-500 uppercase dark:text-gray-400">Received</th>
              <th class="p-3 text-right text-xs font-medium text-gray-500 uppercase dark:text-gray-400">Serials</th>
              <th class="p-3 text-left text-xs font-medium text-gray-500 uppercase dark:text-gray-400">UOM</th>
              <th class="p-3 text-left text-xs font-medium text-gray-500 uppercase dark:text-gray-400">PCN / BOM</th>
              <th class="p-3 text-left text-xs font-medium text-gray-500 uppercase dark:text-gray-400">Gate</th>
              <th class="p-3" style="min-width:160px"></th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-200 dark:divide-gray-700">
            <template v-for="(line, idx) in poItems" :key="'po-' + idx">
              <tr class="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                <td class="p-3 text-sm"><a v-if="line.item_code" :href="erpBaseUrl + '/app/item/' + line.item_code" target="_blank" rel="noopener" class="text-blue-600 hover:underline dark:text-blue-400">{{ line.item_code }}</a></td>
                <td v-if="isSubcontracted" class="p-3 text-sm">
                  <a v-if="line.fg_item" :href="erpBaseUrl + '/app/item/' + line.fg_item" target="_blank" rel="noopener" class="text-blue-600 hover:underline dark:text-blue-400">{{ line.fg_item }}</a>
                  <span v-else class="text-gray-400">—</span>
                </td>
                <td class="p-3 text-sm text-gray-900 dark:text-white">{{ line.item_name }}</td>
                <td class="p-3 text-sm text-right text-gray-900 dark:text-white">{{ line.qty != null ? line.qty : '—' }}</td>
                <td v-if="isSubcontracted" class="p-3 text-right text-sm text-gray-900 dark:text-white">{{ line.fg_item_qty != null ? line.fg_item_qty : '—' }}</td>
                <td class="p-3 text-sm text-right text-gray-900 dark:text-white">{{ line.received_qty != null ? line.received_qty : '—' }}</td>
                <td class="p-3 text-sm text-right text-gray-900 dark:text-white">
                  <span v-if="lineSerialCountsLoading" class="text-gray-400">…</span>
                  <span v-else>{{ serialCountForLine(line) }}</span>
                </td>
                <td class="p-3 text-sm text-gray-900 dark:text-white">{{ line.uom || '—' }}</td>
                <td class="p-3 text-sm">
                  <a
                    v-if="line.custom_hardware_pcn"
                    :href="erpBaseUrl + '/app/pcn/' + line.custom_hardware_pcn"
                    target="_blank"
                    rel="noopener"
                    class="text-blue-600 hover:underline dark:text-blue-400"
                  >{{ line.custom_hardware_pcn }}</a>
                  <span v-else class="text-gray-400">—</span>
                </td>
                <td class="p-3 text-sm">
                  <a
                    v-if="line.gate"
                    :href="erpBaseUrl + '/app/gate/' + line.gate.name"
                    target="_blank"
                    rel="noopener"
                    class="text-blue-600 hover:underline dark:text-blue-400"
                  >{{ line.gate.name }}</a>
                  <span v-if="line.gate && line.gate.result" class="ml-1 text-xs text-gray-500 dark:text-gray-400">({{ line.gate.result }})</span>
                  <span v-if="!line.gate" class="text-gray-400">—</span>
                </td>
                <td class="p-3 text-right">
                  <button
                    v-if="isAdmin && !lineSerialCountsLoading && missingCountForLine(line) > 0"
                    :disabled="creatingSerials[isSubcontracted ? (line.fg_item || line.item_code) : line.item_code]"
                    class="inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-md text-white bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-wait"
                    @click="createPreliminarySerials(
                      isSubcontracted ? (line.fg_item || line.item_code) : line.item_code,
                      missingCountForLine(line),
                      line.custom_hardware_pcn || ''
                    )"
                  >
                    <template v-if="creatingSerials[isSubcontracted ? (line.fg_item || line.item_code) : line.item_code]">Creating…</template>
                    <template v-else>Create {{ missingCountForLine(line) }} serials</template>
                  </button>
                </td>
              </tr>
              <!-- PCN detail sub-row -->
              <tr v-if="line.pcn_info" class="bg-gray-50/60 dark:bg-gray-700/30">
                <td :colspan="colCount" class="px-6 py-3">
                  <div class="flex flex-wrap gap-4 text-sm">
                    <span v-if="line.pcn_info.hardware_version" class="text-gray-700 dark:text-gray-300">
                      <strong class="text-gray-500 dark:text-gray-400">HW Version:</strong> {{ line.pcn_info.hardware_version }}
                    </span>
                    <span v-if="line.pcn_info.bom" class="text-gray-700 dark:text-gray-300">
                      <strong class="text-gray-500 dark:text-gray-400">BOM:</strong>
                      <a
                        :href="erpBaseUrl + '/app/bom/' + line.pcn_info.bom"
                        target="_blank"
                        rel="noopener"
                        class="text-blue-600 hover:underline dark:text-blue-400"
                      >{{ line.pcn_info.bom }}</a>
                    </span>
                  </div>
                  <div v-if="line.pcn_info.description_of_change" class="mt-2 text-sm text-gray-700 dark:text-gray-300 prose prose-sm dark:prose-invert max-w-none" v-html="line.pcn_info.description_of_change"></div>
                </td>
              </tr>
            </template>
            <!-- Supplier-sourced serialized BOM items inline -->
            <template v-if="supplierSourcedItems.length">
              <tr class="bg-amber-50/70 dark:bg-amber-900/15">
                <td :colspan="colCount" class="px-3 py-2">
                  <span class="text-xs font-semibold text-amber-800 dark:text-amber-300 uppercase tracking-wide">Supplier-sourced serialized items (from BOM)</span>
                </td>
              </tr>
              <tr v-for="(si, idx) in supplierSourcedItems" :key="'ss-' + idx" class="bg-amber-50/30 hover:bg-amber-50 dark:bg-amber-900/5 dark:hover:bg-amber-900/10">
                <td class="p-3 text-sm"><a v-if="si.item_code" :href="erpBaseUrl + '/app/item/' + si.item_code" target="_blank" rel="noopener" class="text-blue-600 hover:underline dark:text-blue-400">{{ si.item_code }}</a></td>
                <td v-if="isSubcontracted" class="p-3 text-sm text-gray-400">—</td>
                <td class="p-3 text-sm text-gray-900 dark:text-white">{{ si.item_name }}</td>
                <td class="p-3 text-sm text-right text-gray-500 dark:text-gray-400" :title="'BOM qty: ' + (si.qty != null ? si.qty : '?') + ' × FG qty'">{{ si.expected_qty != null ? si.expected_qty : '—' }}</td>
                <td v-if="isSubcontracted" class="p-3 text-sm text-right text-gray-500 dark:text-gray-400">{{ si.qty != null ? si.qty : '—' }}/unit</td>
                <td class="p-3"></td>
                <td class="p-3 text-sm text-right text-gray-900 dark:text-white">
                  <span v-if="lineSerialCountsLoading" class="text-gray-400">…</span>
                  <span v-else>{{ serialCountByItemCode[si.item_code] || 0 }}</span>
                </td>
                <td class="p-3"></td>
                <td class="p-3 text-sm">
                  <a
                    v-if="si.pcn"
                    :href="erpBaseUrl + '/app/pcn/' + si.pcn"
                    target="_blank"
                    rel="noopener"
                    class="text-blue-600 hover:underline dark:text-blue-400"
                  >{{ si.pcn }}</a>
                  <template v-if="si.pcn && si.bom_name"> / </template>
                  <a
                    v-if="si.bom_name"
                    :href="erpBaseUrl + '/app/bom/' + si.bom_name"
                    target="_blank"
                    rel="noopener"
                    class="text-blue-600 hover:underline dark:text-blue-400"
                  >{{ si.bom_name }}</a>
                  <span v-if="!si.pcn && !si.bom_name" class="text-gray-400">—</span>
                </td>
                <td class="p-3 text-sm">
                  <a
                    v-if="si.gate"
                    :href="erpBaseUrl + '/app/gate/' + si.gate.name"
                    target="_blank"
                    rel="noopener"
                    class="text-blue-600 hover:underline dark:text-blue-400"
                  >{{ si.gate.name }}</a>
                  <span v-if="si.gate && si.gate.result" class="ml-1 text-xs text-gray-500 dark:text-gray-400">({{ si.gate.result }})</span>
                  <span v-if="!si.gate" class="text-gray-400">—</span>
                </td>
                <td class="p-3 space-y-1">
                  <span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-300">Sourced by supplier</span>
                  <button
                    v-if="isAdmin && !lineSerialCountsLoading && missingCountForSupplierItem(si) > 0"
                    :disabled="creatingSerials[si.item_code]"
                    class="inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-md text-white bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-wait"
                    @click="createPreliminarySerials(si.item_code, missingCountForSupplierItem(si), si.pcn || '')"
                  >
                    <template v-if="creatingSerials[si.item_code]">Creating…</template>
                    <template v-else>Create {{ missingCountForSupplierItem(si) }} serials</template>
                  </button>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
        </div>
      </div>
      <p v-else class="text-sm text-gray-500 dark:text-gray-400">No line items on this purchase order.</p>
    </div>

    <!-- Instructions (collapsed) -->
    <details class="bg-blue-100 border-l-4 border-blue-500 text-blue-900 p-4 rounded-lg shadow-md dark:bg-gray-800 mb-4">
      <summary class="cursor-pointer select-none text-lg font-semibold dark:text-white">
        Instructions (click to expand)
      </summary>
      <div class="mt-3">
        <ol class="list-decimal list-inside text-base font-normal text-gray-800 dark:text-gray-300 space-y-1">
          <li>
            Install <strong>QZ Tray</strong> from
            <a href="https://qz.io/download/" class="text-blue-600 hover:underline" target="_blank" rel="noopener">qz.io/download</a>
          </li>
          <li>
            Setup certificates using the provided script:
            <ul class="list-disc list-inside ml-6 space-y-1">
              <li>
                <a href="/docs/qz-cert-setup-script/" class="text-blue-600 hover:underline" target="_blank" rel="noopener">
                  Download installer ZIP
                </a>
              </li>
              <li>Unpack and run <code>windows-install.bat</code> with Administrator rights.</li>
              <li>Restart QZ Tray afterwards.</li>
              <li>Verify in QZ Tray → Advanced → Site Manager that the certificate is installed.</li>
            </ul>
          </li>
          <li>Use the controls below to select a printer and print labels.</li>
        </ol>
      </div>
    </details>

    <!-- QZ Tray Connection Bar -->
    <div class="p-4 bg-white border mt-4 border-gray-200 rounded-lg shadow-sm dark:border-gray-700 sm:p-6 dark:bg-gray-800">
      <div class="flex items-center justify-between">
        <button :class="connectBtnClass" @click="toggleQz">
          {{ qzConnected ? 'Disconnect' : 'Connect' }}
        </button>
        <div class="space-x-2">
          <button id="refreshPrintersBtn" class="px-3 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 text-sm font-medium" @click="refreshPrinters">
            Refresh Printers
          </button>
        </div>
      </div>

      <!-- Printer Name Selection -->
      <div class="flex items-center space-x-4 mt-4">
        <label class="text-sm font-medium text-gray-700 dark:text-gray-200">Printer Name:</label>
        <select v-model="printerName" @change="autoSetDpiFromPrinter" class="px-2 py-1 border rounded-md text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500">
          <option v-for="p in printers" :key="p" :value="p">{{ p }}</option>
        </select>
      </div>

      <!-- DPI Selection -->
      <div class="flex items-center space-x-4 mt-4">
        <label class="text-sm font-medium text-gray-700 dark:text-gray-200">DPI:</label>
        <select v-model="dpi" class="px-2 py-1 border rounded-md text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500">
          <option value="203">203</option>
          <option value="300">300</option>
        </select>
      </div>

      <!-- ZPL Adjustments -->
      <div class="flex items-center space-x-6 mt-4">
        <label class="text-sm font-medium text-gray-700 dark:text-gray-200">Adjustments:</label>
        <div class="flex items-center space-x-2">
          <span class="text-sm text-gray-700 dark:text-gray-200">^LS (left/right)</span>
          <input type="number" v-model.number="offsetLS" min="-120" max="120" step="1" class="px-2 py-1 w-28 border rounded-md text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500" />
        </div>
        <div class="flex items-center space-x-2">
          <span class="text-sm text-gray-700 dark:text-gray-200">^LT (up/down)</span>
          <input type="number" v-model.number="offsetLT" min="-120" max="120" step="1" class="px-2 py-1 w-28 border rounded-md text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500" />
        </div>
      </div>

      <!-- Messages -->
      <div id="messageDisplay" class="p-4 bg-gray-100 border border-gray-200 rounded-lg shadow-sm dark:bg-gray-800 dark:border-gray-700 mt-4">
        <h4 class="mb-2 text-lg font-bold text-gray-900 dark:text-white">Messages:</h4>
        <div class="text-gray-700 dark:text-gray-300 space-y-1 max-h-64 overflow-y-auto">
          <div v-for="(m, i) in messages" :key="i">{{ m }}</div>
        </div>
      </div>
    </div>

    <!-- Serials Table -->
    <div class="p-4 bg-white border mt-4 border-gray-200 rounded-lg shadow-sm dark:border-gray-700 sm:p-6 dark:bg-gray-800">
      <h3 class="text-xl font-bold text-gray-900 dark:text-white mb-4">Serials for {{ serialsSectionTitle }}</h3>

      <div class="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div class="flex items-center gap-3 flex-wrap">
          <button type="button" class="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 focus:ring-2 focus:ring-gray-400 disabled:opacity-50" :disabled="!visibleSerials.length" @click="toggleSelectAll">
            {{ allSelected ? 'Unselect page' : 'Select page' }}
          </button>

          <!-- Filters -->
          <input v-model="serialFilters.itemCode" placeholder="Filter item code" class="px-2 py-1 border rounded-md text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500" @input="onSerialFilterInput" />
          <input v-model="serialFilters.labelName" placeholder="Filter label name" class="px-2 py-1 border rounded-md text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500" @input="onSerialFilterInput" />
          <select v-model="serialFilters.labelSize" class="px-2 py-1 border rounded-md text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500" @change="applySerialFilters">
            <option value="">All sizes</option>
            <option value="Standard">Standard</option>
            <option value="Small">Small</option>
            <option value="Cable Flag">Cable Flag</option>
          </select>

          <label class="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
            <input type="checkbox" v-model="excludePrinted" @change="onExcludePrintedChange" />
            Exclude already printed
          </label>

          <div class="flex items-center gap-2">
            <label class="text-sm text-gray-700 dark:text-gray-200" for="serial-page-size">Rows</label>
            <select
              id="serial-page-size"
              v-model="serialPageSizeChoice"
              class="px-2 py-1 border rounded-md text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              @change="onSerialPageSizeChoiceChange"
            >
              <option v-for="size in serialPageSizeOptions" :key="size" :value="String(size)">{{ size }}</option>
              <option value="custom">Custom</option>
            </select>
            <input
              v-if="serialPageSizeChoice === 'custom'"
              v-model="serialCustomPageSize"
              type="number"
              min="1"
              :max="serialMaxPageSize"
              class="px-2 py-1 w-20 border rounded-md text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              aria-label="Custom rows per page"
              @change="applyCustomSerialPageSize"
            />
          </div>
        </div>

        <div class="flex items-center space-x-4">
          <span class="text-sm font-medium text-gray-700 dark:text-gray-200">
            Selected / Printed: {{ selectedSerials.length }} / {{ printCount }}
          </span>

          <button type="button" class="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:ring-4 focus:ring-blue-300 text-base font-medium" @click="printSelected">
            Print
          </button>
        </div>
      </div>

      <div
        v-if="serialsLoading"
        class="py-14 flex flex-col items-center justify-center rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50/80 dark:bg-gray-900/30"
        role="status"
        aria-live="polite"
        aria-busy="true"
      >
        <svg
          class="h-10 w-10 animate-spin text-blue-600 dark:text-blue-400"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
          <path
            class="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
        <p class="mt-4 text-sm font-medium text-gray-700 dark:text-gray-300">Loading serials…</p>
        <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">Loading this page only.</p>
      </div>

      <div
        v-else-if="!visibleSerials.length"
        class="py-10 text-center rounded-lg border border-dashed border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-900/40"
      >
        <p class="text-base font-medium text-gray-700 dark:text-gray-300">No serials found</p>
        <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
          <template v-if="serialFiltersActive">No serials match these filters.</template>
          <template v-else>
            There are no serial numbers linked to this document yet.
            <span v-if="isPurchaseOrder">Serials must reference this PO via <code class="text-xs bg-gray-200 dark:bg-gray-700 px-1 rounded">custom_purchase_order</code>.</span>
          </template>
        </p>
      </div>

      <div v-else class="flex flex-col">
        <div class="overflow-x-auto">
          <div class="inline-block min-w-full align-middle">
            <div class="overflow-hidden shadow">
              <table class="min-w-full divide-y divide-gray-200 table-fixed dark:divide-gray-600">
                <thead class="bg-gray-100 dark:bg-gray-700">
                  <tr>
                    <th scope="col" class="p-4 text-xs font-medium text-left text-gray-500 uppercase dark:text-gray-400">Select</th>
                    <th scope="col" class="p-4 text-xs font-medium text-left text-gray-500 uppercase dark:text-gray-400">Serial No</th>
                    <th scope="col" class="p-4 text-xs font-medium text-left text-gray-500 uppercase dark:text-gray-400">Item Code</th>
                    <th scope="col" class="p-4 text-xs font-medium text-left text-gray-500 uppercase dark:text-gray-400">Item Name</th>
                    <th scope="col" class="p-4 text-xs font-medium text-left text-gray-500 uppercase dark:text-gray-400">Label Name</th>
                    <th scope="col" class="p-4 text-xs font-medium text-left text-gray-500 uppercase dark:text-gray-400">Label Size</th>
                    <th scope="col" class="p-4 text-xs font-medium text-left text-gray-500 uppercase dark:text-gray-400">Printed</th>
                    <th v-if="supplierSourcedItemCodes.length" scope="col" class="p-4 text-xs font-medium text-left text-gray-500 uppercase dark:text-gray-400">Source</th>
                  </tr>
                </thead>
                <tbody class="bg-white divide-y divide-gray-200 dark:bg-gray-800 dark:divide-gray-700">
                  <tr v-for="sn in visibleSerials" :key="sn.serial_no" 
                      :class="[
                        'select-none cursor-pointer transition-colors duration-150',
                        isSelected(sn) 
                          ? 'bg-blue-50 dark:bg-blue-900/20' 
                          : 'hover:bg-gray-100 dark:hover:bg-gray-700'
                      ]"
                      @mouseenter="onRowMouseEnter(sn)"
                      @mousedown="onRowMouseDown(sn, $event)"
                      @mouseup="onRowMouseUp(sn, $event)">
                    <td :class="[
                          'p-4 text-sm text-gray-900 dark:text-white border-l-4',
                          isSelected(sn) ? 'border-blue-500' : 'border-transparent'
                        ]">
                      <div class="w-5 h-5 rounded border-2 flex items-center justify-center transition-all duration-150" 
                           :class="isSelected(sn) 
                             ? 'bg-blue-600 border-blue-600 shadow-md' 
                             : 'border-gray-300 dark:border-gray-600 hover:border-blue-400'">
                        <svg v-if="isSelected(sn)" class="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"></path>
                        </svg>
                      </div>
                    </td>
                    <td class="p-4 text-sm text-gray-900 dark:text-white">{{ sn.serial_no }}</td>
                    <td class="p-4 text-sm text-gray-900 dark:text-white">{{ sn.item_code }}</td>
                    <td class="p-4 text-sm text-gray-900 dark:text-white">{{ sn.item_name }}</td>
                    <td class="p-4 text-sm text-gray-900 dark:text-white">{{ sn.label_name || '-' }}</td>
                    <td class="p-4 text-sm text-gray-900 dark:text-white">{{ sn.label_size || '-' }}</td>
                    <td class="p-4 text-sm text-gray-900 dark:text-white">{{ sn.printed_count || 0 }}</td>
                    <td v-if="supplierSourcedItemCodes.length" class="p-4 text-sm">
                      <span v-if="sn.sourced_by_supplier" class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-300">Supplier</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <div class="mt-3 flex items-center gap-2 flex-wrap">
        <button type="button" class="px-3 py-1.5 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 disabled:opacity-50" :disabled="serialsLoading || serialPage<=1" @click="changeSerialPage(serialPage-1)">Prev</button>
        <span class="text-sm text-gray-600 dark:text-gray-300">
          Page {{ serialPage }}<template v-if="pagerTotalPages"> / {{ pagerTotalPages }}</template>
          <template v-if="pagerTotal != null"> · {{ pagerTotal }} serials</template>
        </span>
        <button type="button" class="px-3 py-1.5 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 disabled:opacity-50" :disabled="serialsLoading || !pagerHasNext" @click="changeSerialPage(serialPage+1)">Next</button>
      </div>
    </div>

  </div>
</template>
<script>
export default {
  name: 'AppLabelPrintDetail',
  data() {
    return {
      receiptName: '',
      docType: 'Purchase Receipt',
      meta: null,
      poItems: [],
      poDetailMeta: null,
      poDetailLoading: false,
      poDetailError: null,
      isSubcontracted: false,
      erpBaseUrl: '',
      supplierSourcedItems: [],
      supplierSourcedItemCodes: [],
      isAdmin: false,
      creatingSerials: {},
      qzConnected: false,
      printers: [],
      printerName: '',
      dpi: '203',
      messages: [],
      serials: [],
      serialsLoading: true,
      serverPaged: false,
      serialPage: 1,
      serialPageSizeOptions: [10, 20, 50, 100, 250, 500],
      serialMaxPageSize: 500,
      serialPageSizeChoice: '10',
      serialCustomPageSize: 10,
      serialPageSize: 10,
      serialTotal: null,
      serialTotalPages: null,
      serialHasNext: false,
      countsByItem: null,
      serialFilterTimer: null,
      serialFetchSeq: 0,
      selectedSerials: [],
      serialFilters: { itemCode: '', labelName: '', labelSize: '' },
      excludePrinted: false,
      printCount: 0,
      // ZPL offsets (applied to existing ^LS/^LT only)
      offsetLS: 0,
      offsetLT: 0,
      // Drag selection state
      isDragging: false,
      dragStartSerial: null,
      dragMode: null, // 'select' | 'deselect'
      mouseDownSerial: null // Track which serial was clicked
    };
  },

  computed: {
    isPurchaseOrder() {
      return this.docType === 'Purchase Order';
    },
    colCount() {
      return this.isSubcontracted ? 11 : 9;
    },
    serialCountByItemCode() {
      if (this.isPurchaseOrder && this.countsByItem) return this.countsByItem;
      const counts = {};
      for (const sn of this.serials) {
        const code = sn.item_code || '';
        counts[code] = (counts[code] || 0) + 1;
      }
      return counts;
    },
    countItemCodes() {
      const codes = new Set();
      for (const line of this.poItems) {
        const code = this.isSubcontracted ? (line.fg_item || line.item_code) : line.item_code;
        if (code) codes.add(code);
      }
      for (const si of this.supplierSourcedItems) {
        if (si.item_code) codes.add(si.item_code);
      }
      return [...codes];
    },
    serialFiltersActive() {
      return !!(
        (this.serialFilters.itemCode || '').trim()
        || (this.serialFilters.labelName || '').trim()
        || this.serialFilters.labelSize
        || this.excludePrinted
      );
    },
    filteredSerials() {
      if (this.serverPaged) return this.serials;
      const ic = (this.serialFilters.itemCode || '').toLowerCase();
      const ln = (this.serialFilters.labelName || '').toLowerCase();
      const ls = (this.serialFilters.labelSize || '');
      return this.serials.filter(s => {
        const matchItem = !ic || (s.item_code || '').toLowerCase().includes(ic);
        const matchName = !ln || (s.label_name || '').toLowerCase().includes(ln);
        const matchSize = !ls || (s.label_size || '') === ls;
        return matchItem && matchName && matchSize;
      });
    },
    visibleSerials() {
      if (this.serverPaged) return this.serials;
      const start = (this.serialPage - 1) * this.serialPageSize;
      return this.filteredSerials.slice(start, start + this.serialPageSize);
    },
    lineSerialCountsLoading() {
      return this.isPurchaseOrder && this.countsByItem == null;
    },
    pagerTotal() {
      if (this.serverPaged) return this.serialTotal;
      return this.filteredSerials.length;
    },
    pagerTotalPages() {
      if (this.serverPaged) return this.serialTotalPages;
      return Math.ceil(this.filteredSerials.length / this.serialPageSize) || 1;
    },
    pagerHasNext() {
      if (this.serverPaged) return this.serialHasNext;
      return this.serialPage * this.serialPageSize < this.filteredSerials.length;
    },
    docHeading() {
      return this.isPurchaseOrder
        ? `Purchase Order: ${this.receiptName}`
        : `Purchase Receipt: ${this.receiptName}`;
    },
    docDetailsTitle() {
      return this.isPurchaseOrder ? 'Order details' : 'Receipt details';
    },
    docIdLabel() {
      return this.isPurchaseOrder ? 'Order ID' : 'Receipt ID';
    },
    serialsSectionTitle() {
      return this.isPurchaseOrder
        ? `PO ${this.receiptName}`
        : this.receiptName;
    },
    listMetaJsonPath() {
      return this.isPurchaseOrder
        ? '/erp/purchase-orders.json'
        : '/erp/purchase-receipts.json';
    },
    metaGridClass() {
      const hasCustomer = this.isPurchaseOrder && this.meta && (this.meta.customer_name || '').trim();
      return hasCustomer
        ? 'grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4'
        : 'grid grid-cols-1 md:grid-cols-3 gap-4';
    },
    connectBtnClass() {
      return [
        'px-4','py-2','text-white','rounded-lg','text-sm','font-medium',
        this.qzConnected ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'
      ];
    },
    allSelected() {
      return this.visibleSerials.length > 0 && this.visibleSerials.every(s => this.isSelected(s));
    }
  },

  async mounted() {
    const el = document.getElementById('vue-label-print-detail');
    this.receiptName = el?.dataset?.receiptName || '';
    this.docType = el?.dataset?.docType || 'Purchase Receipt';
    this.isAdmin = el?.dataset?.isAdmin === '1';
    await this.fetchMeta();
    if (this.isPurchaseOrder) {
      await this.fetchPurchaseOrderDetail();
    }
    await this.loadSerials();
    this.loadSerialCounts();

    // 1) Wait until qz-tray.js is present
    await this.ensureQzReady().catch(() => this.addMessage('QZ library not found'));

    // 2) Register security hooks BEFORE connecting
    this.setupQzSecurity();

    // 3) Connect (only once)
    if (!window.qz || window.qz.websocket.isActive()) return;
    try {
      await this.connectQz();
    } catch (_) {
      this.addMessage('Auto-connect to QZ failed');
    }

    // Global mouseup to end drag even outside table
    window.addEventListener('mouseup', this.endDragSelection);
  },

  beforeUnmount() {
    // optional: clean disconnect
    if (window.qz && window.qz.websocket.isActive()) {
      window.qz.websocket.disconnect().catch(() => {});
    }

    window.removeEventListener('mouseup', this.endDragSelection);
    clearTimeout(this.serialFilterTimer);
  },

  methods: {
    isSelected(serial) {
      return this.selectedSerials.some(s => s.serial_no === serial.serial_no);
    },

    toggleSelection(serial) {
      const index = this.selectedSerials.findIndex(s => s.serial_no === serial.serial_no);
      if (index >= 0) {
        this.selectedSerials.splice(index, 1);
      } else {
        this.selectedSerials.push(serial);
      }
    },

    addMessage(m) { this.messages.push(m); },

    async ensureQzReady(timeoutMs = 5000) {
      const start = Date.now();
      return new Promise((resolve, reject) => {
        const t = setInterval(() => {
          if (window.qz && window.qz.security && window.qz.websocket) {
            clearInterval(t);
            resolve();
          } else if (Date.now() - start > timeoutMs) {
            clearInterval(t);
            reject(new Error('QZ not loaded'));
          }
        }, 50);
      });
    },

    setupQzSecurity() {
      if (!window.qz || !window.qz.security) return;

      // Helpful while testing
      if (window.qz.api && window.qz.api.setLogLevel) {
        window.qz.api.setLogLevel('debug');
      }

      // Hand the LEAF certificate to QZ (resolver style)
      window.qz.security.setCertificatePromise(function (resolve, reject) {
        fetch('/qz/digital-certificate.txt', {
          cache: 'no-store',
          credentials: 'same-origin'
        })
          .then(r => (r.ok ? r.text() : Promise.reject(new Error('cert fetch failed'))))
          .then(txt => resolve(txt))
          .catch(err => reject(err));
      });

      // Sign with SHA-512 (server returns base64)
      window.qz.security.setSignatureAlgorithm('SHA512');
      window.qz.security.setSignaturePromise(function (toSign) {
        return function (resolve, reject) {
          fetch('/qz/sign', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'same-origin',
            body: JSON.stringify({ toSign })
          })
            .then(r => (r.ok ? r.text() : Promise.reject(new Error('sign failed'))))
            .then(sig => resolve(sig))
            .catch(err => reject(err));
        };
      });
    },

    async toggleQz() {
      if (!window.qz) { this.addMessage('QZ Tray library not loaded'); return; }
      if (this.qzConnected) { return this.disconnectQz(); }
      return this.connectQz();
    },

    async connectQz() {
      if (!window.qz) throw new Error('QZ not available');
      if (window.qz.websocket.isActive()) return;

      await window.qz.websocket.connect();
      this.qzConnected = true;
      this.addMessage('QZ Tray connected');
      await this.refreshPrinters();
    },

    async disconnectQz() {
      try {
        await window.qz.websocket.disconnect();
        this.qzConnected = false;
        this.addMessage('QZ Tray disconnected');
      } catch (e) {
        this.addMessage('Failed to disconnect QZ: ' + e);
      }
    },

    async refreshPrinters() {
      if (!this.qzConnected) { this.addMessage('Connect to QZ first'); return; }
      try {
        const printers = await window.qz.printers.find();
        this.printers = printers;
        this.printerName = printers[0] || '';
        this.autoSetDpiFromPrinter();
        this.addMessage('Printers loaded: ' + printers.length);
      } catch (e) {
        this.addMessage('Failed to load printers: ' + e);
      }
    },

    autoSetDpiFromPrinter() {
      const name = (this.printerName || '').toLowerCase();
      if (name.includes('300dpi') || name.includes('300 dpi') || name.includes('dpi300') || name.includes('zpl300')) {
        this.dpi = '300';
      } else if (name.includes('203dpi') || name.includes('203 dpi') || name.includes('dpi203') || name.includes('zpl203')) {
        this.dpi = '203';
      }
    },

    formatDate(raw) {
      if (!raw) return '—';
      const d = new Date(raw);
      if (isNaN(d.getTime())) return raw;
      return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
    },

    serialCountForLine(line) {
      const code = this.isSubcontracted ? (line.fg_item || line.item_code) : line.item_code;
      return this.serialCountByItemCode[code] || 0;
    },

    missingCountForLine(line) {
      const code = this.isSubcontracted ? (line.fg_item || line.item_code) : line.item_code;
      const expected = this.isSubcontracted ? (line.fg_item_qty || 0) : (line.qty || 0);
      const found = this.serialCountByItemCode[code] || 0;
      return Math.max(0, expected - found);
    },

    missingCountForSupplierItem(si) {
      const found = this.serialCountByItemCode[si.item_code] || 0;
      return Math.max(0, (si.expected_qty || 0) - found);
    },

    pcnForItemCode(itemCode) {
      for (const line of this.poItems) {
        const code = this.isSubcontracted ? (line.fg_item || line.item_code) : line.item_code;
        if (code === itemCode && line.custom_hardware_pcn) return line.custom_hardware_pcn;
      }
      return '';
    },

    async createPreliminarySerials(itemCode, qty, pcn) {
      if (!confirm(`Create ${qty} preliminary serial(s) for ${itemCode}?`)) return;
      this.$set ? this.$set(this.creatingSerials, itemCode, true) : (this.creatingSerials[itemCode] = true);
      this.creatingSerials = { ...this.creatingSerials };
      try {
        const csrfToken = document.querySelector('[name=csrfmiddlewaretoken]')?.value
          || document.cookie.split('; ').find(c => c.startsWith('csrftoken='))?.split('=')[1] || '';
        const res = await fetch('/erp/create-preliminary-serials/', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', 'X-CSRFToken': csrfToken },
          body: JSON.stringify({
            item_code: itemCode,
            qty: qty,
            purchase_order: this.receiptName,
            pcn: pcn || '',
          }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          this.addMessage(`Failed to create serials for ${itemCode}: ${data.error || res.status}`);
          return;
        }
        this.addMessage(`Created ${data.created || 0} serial(s) for ${itemCode}`);
        this.countsByItem = null;
        this.loadSerialCounts();
        if (data.errors && data.errors.length) {
          this.addMessage(`${data.errors.length} serial(s) failed to link: ${data.errors.map(e => e.serial).join(', ')}`);
        }
        await this.loadSerials({ showLoading: false });
      } catch (e) {
        this.addMessage(`Error creating serials: ${e && e.message ? e.message : e}`);
      } finally {
        this.creatingSerials[itemCode] = false;
        this.creatingSerials = { ...this.creatingSerials };
      }
    },

    supplierDisplay(row) {
      if (!row) return '—';
      const id = (row.supplier || '').trim();
      const friendly = (row.supplier_name || '').trim();
      if (friendly && id && friendly !== id) {
        return `${friendly} (${id})`;
      }
      return friendly || id || '—';
    },

    async fetchMeta() {
      const url = new URL(this.listMetaJsonPath, window.location.origin);
      url.searchParams.set('name', this.receiptName);
      url.searchParams.set('page', '1');
      url.searchParams.set('page_size', '1');
      const res = await fetch(url.toString(), { credentials: 'same-origin' });
      if (!res.ok) return;
      const data = await res.json();
      this.meta = (data.results || []).find(r => r.name === this.receiptName) || null;
    },

    async fetchPurchaseOrderDetail() {
      if (!this.receiptName) return;
      this.poDetailLoading = true;
      this.poDetailError = null;
      this.poItems = [];
      this.poDetailMeta = null;
      try {
        const url = new URL('/erp/purchase-order-detail.json', window.location.origin);
        url.searchParams.set('name', this.receiptName);
        const res = await fetch(url.toString(), { credentials: 'same-origin' });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          this.poDetailError = data.error || `Could not load order (${res.status})`;
          return;
        }
        this.poItems = Array.isArray(data.items) ? data.items : [];
        this.poDetailMeta = {
          status: data.status,
          transaction_date: data.transaction_date,
        };
        this.isSubcontracted = !!data.is_subcontracted;
        this.erpBaseUrl = data.erp_base_url || '';
        this.supplierSourcedItems = Array.isArray(data.supplier_sourced_items) ? data.supplier_sourced_items : [];
        this.supplierSourcedItemCodes = Array.isArray(data.supplier_sourced_item_codes) ? data.supplier_sourced_item_codes : [];
      } catch (e) {
        this.poDetailError = e && e.message ? String(e.message) : 'Failed to load purchase order';
      } finally {
        this.poDetailLoading = false;
      }
    },

    /**
     * @param {{ showLoading?: boolean }} opts - showLoading false = background refresh (e.g. after print) without full-page spinner
     */
    commitSerialPageSize() {
      if (this.serialPageSizeChoice !== 'custom') {
        return parseInt(this.serialPageSizeChoice, 10);
      }
      const parsed = parseInt(this.serialCustomPageSize, 10);
      const size = Number.isFinite(parsed) && parsed >= 1
        ? Math.min(parsed, this.serialMaxPageSize)
        : this.serialPageSize;
      this.serialCustomPageSize = size;
      if (this.serialPageSizeOptions.includes(size)) {
        this.serialPageSizeChoice = String(size);
      }
      return size;
    },
    onSerialPageSizeChoiceChange() {
      if (this.serialPageSizeChoice === 'custom') {
        this.serialCustomPageSize = this.serialPageSize;
        return;
      }
      const size = this.commitSerialPageSize();
      if (size === this.serialPageSize) return;
      this.serialPageSize = size;
      this.serialPage = 1;
      this.selectedSerials = [];
      if (this.isPurchaseOrder) this.loadSerials();
    },
    applyCustomSerialPageSize() {
      const size = this.commitSerialPageSize();
      if (size === this.serialPageSize) return;
      this.serialPageSize = size;
      this.serialPage = 1;
      this.selectedSerials = [];
      if (this.isPurchaseOrder) this.loadSerials();
    },
    onSerialFilterInput() {
      this.serialPage = 1;
      if (!this.isPurchaseOrder) return;
      clearTimeout(this.serialFilterTimer);
      this.serialFilterTimer = setTimeout(() => {
        this.selectedSerials = [];
        this.loadSerials();
      }, 350);
    },
    applySerialFilters() {
      clearTimeout(this.serialFilterTimer);
      this.serialPage = 1;
      this.selectedSerials = [];
      if (this.isPurchaseOrder) this.loadSerials();
    },
    onExcludePrintedChange() {
      clearTimeout(this.serialFilterTimer);
      this.serialPage = 1;
      this.selectedSerials = [];
      this.loadSerials();
    },
    async loadSerialCounts() {
      if (!this.isPurchaseOrder || !this.receiptName) return;
      if (!this.countItemCodes.length) {
        this.countsByItem = {};
        return;
      }
      const countRequest = (this.serialCountFetchSeq = (this.serialCountFetchSeq || 0) + 1);
      try {
        const url = new URL('/erp/receipt-serials.json', window.location.origin);
        url.searchParams.set('selected_receipt', this.receiptName);
        url.searchParams.set('doc_type', this.docType);
        url.searchParams.set('counts_only', '1');
        url.searchParams.set('count_item_codes', this.countItemCodes.join(','));
        const res = await fetch(url.toString(), { credentials: 'same-origin' });
        if (countRequest !== this.serialCountFetchSeq) return;
        if (!res.ok) {
          if (this.countsByItem == null) this.countsByItem = {};
          return;
        }
        const data = await res.json();
        if (countRequest !== this.serialCountFetchSeq) return;
        this.countsByItem = data.counts_by_item || {};
      } catch (e) {
        if (countRequest === this.serialCountFetchSeq && this.countsByItem == null) {
          this.countsByItem = {};
        }
      }
    },
    changeSerialPage(page) {
      if (page < 1 || page === this.serialPage) return;
      this.serialPage = page;
      if (this.isPurchaseOrder) this.loadSerials({ keepSelection: true });
    },
    async loadSerials(opts = {}) {
      const showLoading = opts.showLoading !== false;
      const keepSelection = !!opts.keepSelection;
      if (!this.receiptName) {
        this.serials = [];
        this.serialsLoading = false;
        return;
      }
      const requestId = ++this.serialFetchSeq;
      if (showLoading) {
        this.serialsLoading = true;
      }
      try {
        const url = new URL('/erp/receipt-serials.json', window.location.origin);
        url.searchParams.set('selected_receipt', this.receiptName);
        url.searchParams.set('doc_type', this.docType);
        url.searchParams.set('exclude_printed', this.excludePrinted ? '1' : '0');
        if (this.supplierSourcedItemCodes.length) {
          url.searchParams.set('supplier_sourced_item_codes', this.supplierSourcedItemCodes.join(','));
        }
        if (this.isPurchaseOrder) {
          url.searchParams.set('page', String(this.serialPage));
          url.searchParams.set('page_size', String(this.serialPageSize));
          url.searchParams.set('item_code', this.serialFilters.itemCode || '');
          url.searchParams.set('label_name', this.serialFilters.labelName || '');
          url.searchParams.set('label_size', this.serialFilters.labelSize || '');
        }
        const res = await fetch(url.toString(), { credentials: 'same-origin' });
        if (requestId !== this.serialFetchSeq) return;
        if (!res.ok) {
          this.addMessage('Failed to load serials');
          this.serials = [];
          this.serialHasNext = false;
          return;
        }
        const data = await res.json();
        if (requestId !== this.serialFetchSeq) return;
        const previous = new Map(this.selectedSerials.map(s => [s.serial_no, s]));
        this.serials = data.serials || [];
        this.serverPaged = !!data.paged;
        if (data.paged) {
          this.serialTotal = data.total == null ? null : data.total;
          this.serialTotalPages = data.total_pages || null;
          this.serialHasNext = !!data.has_next;
          if (data.page_size && data.page_size !== this.serialPageSize) {
            this.serialPageSize = data.page_size;
            this.serialCustomPageSize = data.page_size;
            this.serialPageSizeChoice = this.serialPageSizeOptions.includes(data.page_size)
              ? String(data.page_size)
              : 'custom';
          }
        } else {
          const pages = Math.ceil(this.filteredSerials.length / this.serialPageSize) || 1;
          this.serialTotal = this.filteredSerials.length;
          this.serialTotalPages = pages;
          this.serialHasNext = this.serialPage < pages;
        }
        if (keepSelection) {
          const fresh = new Map(this.serials.map(s => [s.serial_no, s]));
          this.selectedSerials = this.selectedSerials.map(s => fresh.get(s.serial_no) || previous.get(s.serial_no) || s);
        } else {
          this.selectedSerials = [];
        }
      } catch (e) {
        if (requestId !== this.serialFetchSeq) return;
        this.serials = [];
        this.serialHasNext = false;
        this.addMessage('Failed to load serials: ' + (e && e.message ? e.message : e));
      } finally {
        if (requestId === this.serialFetchSeq && showLoading) {
          this.serialsLoading = false;
        }
      }
    },

    toggleSelectAll() {
      if (this.allSelected) {
        const pageIds = new Set(this.visibleSerials.map(s => s.serial_no));
        this.selectedSerials = this.selectedSerials.filter(s => !pageIds.has(s.serial_no));
      } else {
        for (const serial of this.visibleSerials) {
          if (!this.isSelected(serial)) this.selectedSerials.push(serial);
        }
      }
    },

    endDragSelection() {
      this.isDragging = false;
      this.dragStartSerial = null;
      this.dragMode = null;
      this.mouseDownSerial = null;
    },

    onRowMouseDown(serial, evt) {
      if (evt && evt.button !== 0) return; // left-click only
      this.mouseDownSerial = serial;
      this.dragStartSerial = serial;
      const isSelected = this.selectedSerials.some(s => s.serial_no === serial.serial_no);
      this.dragMode = isSelected ? 'deselect' : 'select';
      // Don't apply selection immediately - wait for mouseup or drag
    },

    onRowMouseUp(serial, evt) {
      if (evt && evt.button !== 0) return; // left-click only
      
      // If we didn't drag (mouseup on same serial as mousedown), toggle selection
      if (!this.isDragging && this.mouseDownSerial && this.mouseDownSerial.serial_no === serial.serial_no) {
        this.toggleSelection(serial);
      }
      
      this.mouseDownSerial = null;
    },

    onRowMouseEnter(serial) {
      // If we have a mousedown serial and we're entering a different serial, start dragging
      if (this.mouseDownSerial && this.mouseDownSerial.serial_no !== serial.serial_no && !this.isDragging) {
        this.isDragging = true;
        // Apply the initial selection to the starting row
        if (this.dragMode === 'select') {
          const isSelected = this.selectedSerials.some(s => s.serial_no === this.dragStartSerial.serial_no);
          if (!isSelected) this.selectedSerials.push(this.dragStartSerial);
        } else {
          const idx = this.selectedSerials.findIndex(s => s.serial_no === this.dragStartSerial.serial_no);
          if (idx >= 0) this.selectedSerials.splice(idx, 1);
        }
      }

      if (!this.isDragging || !this.dragStartSerial || !this.dragMode) return;
      
      // Find range between drag start and current serial
      const startIndex = this.visibleSerials.findIndex(s => s.serial_no === this.dragStartSerial.serial_no);
      const endIndex = this.visibleSerials.findIndex(s => s.serial_no === serial.serial_no);
      
      if (startIndex >= 0 && endIndex >= 0) {
        const minIndex = Math.min(startIndex, endIndex);
        const maxIndex = Math.max(startIndex, endIndex);
        // Apply selection/deselection to range
        for (let i = minIndex; i <= maxIndex; i++) {
          const targetSerial = this.visibleSerials[i];
          const index = this.selectedSerials.findIndex(s => s.serial_no === targetSerial.serial_no);
          if (this.dragMode === 'select' && index < 0) {
            this.selectedSerials.push(targetSerial);
          } else if (this.dragMode === 'deselect' && index >= 0) {
            this.selectedSerials.splice(index, 1);
          }
        }
      }
    },

    async fetchZplTemplate(itemCode, dpi) {
      const url = new URL('/erp/zpl-template/', window.location.origin);
      url.searchParams.set('item_code', itemCode);
      url.searchParams.set('dpi', dpi);
      const res = await fetch(url.toString(), { credentials: 'same-origin' });
      const data = await res.json();
      if (!res.ok) {
        const msg = data && data.error ? data.error : 'Failed to fetch ZPL';
        throw new Error(msg);
      }
      return data;
    },

    fillZpl(template, payload) {
      let out = template;
      Object.entries(payload).forEach(([key, value]) => {
        const safe = value == null ? '' : String(value);
        const escaped = String(key).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        out = out.replace(new RegExp('\\{' + escaped + '\\}', 'gi'), () => safe);
      });
      return out;
    },

    zplFieldsForSerial(s, itemCode) {
      return {
        SERIAL: s.serial_no || '',
        ITEM_NAME: s.item_name || '',
        ITEM_CODE: itemCode || s.item_code || '',
        IMEI: s.imei || '',
        PCN_REVISION: s.pcn_revision || '',
      };
    },

    zplFieldsForSmall(quad, itemCode) {
      const payload = { ITEM_CODE: itemCode || '' };
      for (let i = 0; i < 4; i++) {
        const s = quad[i];
        payload[`SERIAL${i + 1}`] = s?.serial_no || '';
        payload[`IMEI${i + 1}`] = s?.imei || '';
        payload[`PCN_REVISION${i + 1}`] = s?.pcn_revision || '';
      }
      return payload;
    },

    warnMissingZplFields(template, group) {
      if (/\{IMEI\d?\}/i.test(template)) {
        const missing = group.filter(s => s && s.serial_no && !s.imei);
        if (missing.length) {
          this.addMessage(`Warning: IMEI missing for ${missing.length} serial(s): ${missing.map(s => s.serial_no).join(', ')}`);
        }
      }
      if (/\{PCN_REVISION\d?\}/i.test(template)) {
        const missing = group.filter(s => s && s.serial_no && !s.pcn_revision);
        if (missing.length) {
          this.addMessage(`Warning: PCN revision missing for ${missing.length} serial(s): ${missing.map(s => s.serial_no).join(', ')}`);
        }
      }
    },

    chunk(array, size) {
      const chunks = [];
      for (let i = 0; i < array.length; i += size) chunks.push(array.slice(i, i + size));
      return chunks;
    },

    clamp(n, min, max) {
      return Math.min(Math.max(n, min), max);
    },

    adjustZpl(zpl) {
      let out = zpl;
      // Adjust ^LS (horizontal) if present and offset non-zero
      if (this.offsetLS) {
        out = out.replace(/\^LS(-?\d+)/g, (match, val) => {
          const base = parseInt(val, 10);
          if (Number.isNaN(base)) return match;
          const adjusted = this.clamp(base + (parseInt(this.offsetLS, 10) || 0), -9999, 9999);
          return `^LS${adjusted}`;
        });
      }
      // Adjust ^LT (vertical) if present and offset non-zero
      if (this.offsetLT) {
        out = out.replace(/\^LT(-?\d+)/g, (match, val) => {
          const base = parseInt(val, 10);
          if (Number.isNaN(base)) return match;
          const adjusted = this.clamp(base + (parseInt(this.offsetLT, 10) || 0), -120, 120);
          return `^LT${adjusted}`;
        });
      }
      return out;
    },

    sleep(ms) {
      return new Promise(resolve => setTimeout(resolve, ms));
    },

    async printSelected() {
      if (!this.qzConnected) { this.addMessage('Connect to QZ first'); return; }
      if (!this.selectedSerials.length) { this.addMessage('No serials selected'); return; }
      this.printCount = 0;

      const grouped = {};
      this.selectedSerials.forEach(s => {
        if (!grouped[s.item_code]) grouped[s.item_code] = [];
        grouped[s.item_code].push(s);
      });

      const actuallyPrinted = [];
      let printedLabelCount = 0; // count physical labels sent to printer
      for (const [itemCode, group] of Object.entries(grouped)) {
        try {
          const tplResp = await this.fetchZplTemplate(itemCode, this.dpi);
          const template = tplResp.zpl;
          const labelSize = (tplResp.label_size || '').toLowerCase();
          const pauseInterval = parseInt(tplResp.pause_interval || 0, 10);
          this.warnMissingZplFields(template, group);

          const cfg = window.qz.configs.create(this.printerName);
          cfg.reconfigure({ 'raw.encoding': 'UTF-8' });

          if (labelSize === 'small') {
            for (const quad of this.chunk(group, 4)) {
              const zpl = this.adjustZpl(this.fillZpl(template, this.zplFieldsForSmall(quad, itemCode)));
              await window.qz.print(cfg, [{ type: 'raw', format: 'plain', data: zpl }]);
              quad.forEach(s => s && s.serial_no && actuallyPrinted.push(s.serial_no));
              // Increment live printed count by number of serials in this quad
              this.printCount += quad.filter(s => s && s.serial_no).length;

              printedLabelCount += 1;
              if (pauseInterval > 0 && printedLabelCount % pauseInterval === 0) {
                this.addMessage(`Cooling printer for 4s after ${pauseInterval} labels...`);
                await this.sleep(2000);
              }
            }
          } else {
            for (const s of group) {
              const zpl = this.adjustZpl(this.fillZpl(template, this.zplFieldsForSerial(s, itemCode)));
              await window.qz.print(cfg, [{ type: 'raw', format: 'plain', data: zpl }]);
              actuallyPrinted.push(s.serial_no);
              // Increment live printed count per serial
              this.printCount += 1;

              printedLabelCount += 1;
              if (pauseInterval > 0 && printedLabelCount % pauseInterval === 0) {
                this.addMessage(`Cooling printer for 4s after ${pauseInterval} labels...`);
                await this.sleep(2000);
              }
            }
          }
          this.addMessage(`Printed ${group.length} for ${itemCode}`);
        } catch (e) {
          this.addMessage(`Print failed for ${itemCode}: ${e}`);
        }
      }

      if (actuallyPrinted.length) {
        const uniquePrinted = Array.from(new Set(actuallyPrinted));
        const pendingSet = new Set(uniquePrinted);
        // Immediate UI update while ERP request runs
        this.serials = this.serials.map((sn) => {
          if (pendingSet.has(sn.serial_no)) {
            return { ...sn, printed_count: Number(sn.printed_count || 0) + 1 };
          }
          return sn;
        });
        if (this.excludePrinted) {
          this.serials = this.serials.filter((sn) => (sn.printed_count || 0) === 0);
        }
        try {
          const res = await fetch('/erp/serials-printed/', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'same-origin',
            body: JSON.stringify({ serials: uniquePrinted }),
          });
          let body = {};
          try {
            body = await res.json();
          } catch (_) {
            /* non-JSON error body */
          }
          if (!res.ok) {
            this.addMessage(
              'Failed to record printed serials: ' + (body.error || res.statusText || String(res.status)),
            );
            return;
          }
          if (body.missing && body.missing.length) {
            this.addMessage(
              `${body.missing.length} serial(s) not found in ERP (not recorded): ${body.missing.slice(0, 5).join(', ')}${body.missing.length > 5 ? '…' : ''}`,
            );
          }
          if (body.errors && body.errors.length) {
            this.addMessage(`${body.errors.length} serial(s) failed to save in ERP (printed labels may not match records).`);
          }
          // Refresh from ERP so Printed column matches Serial No Detail (authoritative)
          await this.loadSerials({ showLoading: false });
        } catch (e) {
          this.addMessage('Failed to record printed serials: ' + (e && e.message ? e.message : e));
          await this.loadSerials({ showLoading: false });
        }
      }
    }
  }
}
</script>

